/* eslint-disable max-len */
import React, { memo, useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Avatar, { AVATAR_SIZES } from '@jobcase/react_pattern_library/components/Avatar';
import BarsRebrandLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/BarsRebrandLightSVG';
import Column from '@jobcase/react_pattern_library/components/Grid/Column';
import ProfileTooltipMenu from '@jobcase/react_pattern_library/components/ProfileTooltipMenu/ProfileTooltipMenuRebrand';
import rebrandStyles from '@jobcase/react_pattern_library/styles/rebrand';
import Row from '@jobcase/react_pattern_library/components/Grid/Row';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';

import MobileModalHeader from '../../components/MobileModalHeader/MobileModalHeaderRebrand';
import SecondaryNavigation from './SecondaryNavigation/SecondaryNavigationRebrand';
import TertiaryNavigation from './TertiaryNavigation/TertiaryNavigationRebrand';
import { WithGrantsContext } from '../../hoc/withGrants';

import { getCurrentEmployer } from '../../redux/nodes/features/employers/selectors';
import { getCurrentProfile, getIsAuthenticated } from '../../redux/nodes/authentication/selectors';
import { getEmployers } from '../../redux/nodes/entities/employers/selectors';
import { getUnseenCount } from '../../redux/nodes/entities/notifications/selectors';
import { getUnseenEmployerNotificationsCount } from '../../redux/nodes/entities/notifications/actions';
import { routeToProfile, routeToEmployerCenter, routePageBack } from '../../redux/nodes/router/actions';
import { setCurrentEmployer } from '../../redux/nodes/features/employers/actions';
import { getHistory } from '../../redux/nodes/router/selectors';
import { updateFilterOptions } from '../../redux/nodes/features/jobs/actions';
import { getFilterOptions } from '../../redux/nodes/features/jobs/selectors';
import { updateFilterOptions as updateJobGroupsFilterOptions } from '../../redux/nodes/features/jobGroups/actions';
import { getFilterOptions as getJobGroupsFilterOptions } from '../../redux/nodes/features/jobGroups/selectors';

import { CATEGORIES } from '../../utils/constants/siteNotifications';
import { fullName } from '../../utils/profile';
import { getMenuItems } from '../../utils/employerNavigationRebrand';
import { ROUTE_TO_KEY_MAP } from '../../utils/constants/employerNavigation';
import { useSelector, useAppContext } from '../../utils/hooks';

import config from '../../config';
import { omit } from '../../utils/utils';

const PrimaryNavigation = styled.nav`
  align-items: center;
  background-color: ${rebrandStyles.color.jobcaseWhiteColor};
  border-bottom: 1px solid ${rebrandStyles.color.dividerGrayColor};
  display: flex;
  min-height: 60px;

  ${({ isSecondaryNav }) => (isSecondaryNav && css`
    max-height: 60px;
    min-height: 60px;
  `)}

  ${({ isStickyNav }) => (isStickyNav && css`
    position: sticky;
    top: 0;
    z-index: 100;

    @media(min-width: ${rebrandStyles.breakpoint.md}) {
      z-index: 300;
    }
  `)}

  @media(min-width: ${rebrandStyles.breakpoint.md}) {
    min-height: 76px;
  }
`;

const MobileModalHeaderStyled = styled(MobileModalHeader)`
  max-height: 60px;
  min-height: 60px;
  width: 100%;
  border-bottom: 1px solid ${rebrandStyles.color.dividerGrayColor};

  .mobile-modal-header-back {
    margin-top: ${rebrandStyles.spacing.extraExtraTiny}px;
  }
`;

const NavContainer = styled.div`
  height: 60px;
  width: 100%;

  && {
    overflow: hidden;

    @media(min-width: ${rebrandStyles.breakpoint.md}) {
      height: 76px;
      overflow: visible;
      padding: ${rebrandStyles.spacing.tiny + 2}px ${rebrandStyles.spacing.extraTiny * 5}px ${rebrandStyles.spacing.tiny + 2}px ${rebrandStyles.spacing.tiny}px;
    }
  }
`;

const FlexWrapper = styled.div`
  display: flex;

  @media(min-width: ${rebrandStyles.breakpoint.md}) {
    height: 100%;
  }
`;

const FlexWrapperLeft = styled(FlexWrapper)`
  align-items: center;
  justify-content: flex-start;
`;

const FlexWrapperRight = styled(FlexWrapper)`
  align-items: center;
  justify-content: flex-end;
`;

const SVGIconWrapper = styled.div`
  padding-left: ${rebrandStyles.spacing.tiny}px;

  &:hover {
    cursor: pointer;
  }

  svg {
    height: 24px;
    vertical-align: middle;
    width: 24px;

    path {
      fill: ${rebrandStyles.color.textGrayColor};
      stroke-width: 0;
    }
  }
`;

const Image = styled.img`
  height: 22px;
  width: 220px;

  @media (min-width: ${rebrandStyles.breakpoint.md}) {
    height: 21px;
  }
`;

const StyledAvatarWrapper = styled.div`
  position: relative;
`;

const AvatarDot = styled.div`
  background-color: ${rebrandStyles.color.secondaryColor};
  border-radius: 50%;
  bottom: ${rebrandStyles.spacing.extraTiny}px;
  height: 12px;
  position: absolute;
  right: 6px;
  width: 12px;

  @media (min-width: ${rebrandStyles.breakpoint.md}) {
    bottom: 0;
    right: 28px;
  }
`;

const ProfileTooltipMenuWrapper = styled.div`
  border: none;
  position: relative;
`;

const ImageButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  margin-top: ${rebrandStyles.spacing.extraExtraTiny}px;
  padding: 0;

  @media (min-width: ${rebrandStyles.breakpoint.md}) {
    margin-top: 0;
  }
`;

const StyledColumn = styled(Column)`
  align-items: center;
  display: flex;
  flex-direction: column;

  @media (min-width: ${rebrandStyles.breakpoint.md}) {
    flex-direction: row;
  }
`;

const StyledRow = styled(Row)`
  align-items: center;
  height: 100%;
`;

const EmployerNavigation = ({ showSidebar, showProfileMenu, showSecondaryNavigation, onClickBack, backTitle }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isMobile } = useAppContext();

  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [secondaryNavOpen, setSecondaryNavOpen] = useState(false);
  const [tertiaryNavOpen, setTertiaryNavOpen] = useState(false);
  const [menuItemSelected, setMenuItemSelected] = useState('');
  const [currentEmployerSelected, setCurrentEmployerSelected] = useState('');

  const {
    appendEmployerIdInRouteParams,
    handleRoute,
  } = useContext(WithGrantsContext);

  const currentEmployerId = useSelector(getCurrentEmployer);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const originalEmployersData = useSelector(getEmployers);
  const employers = Object.values(originalEmployersData);
  const currentProfile = useSelector(getCurrentProfile);
  const { pathname } = router;

  const history = useSelector(getHistory);
  const filterJobsOptions = useSelector(getFilterOptions);
  const filterJobGroupsOptions = useSelector(getJobGroupsFilterOptions);
  const unseenNotificationCount = useSelector(getUnseenCount);
  const MENU_ITEMS = getMenuItems(unseenNotificationCount);

  // Poll for unseen notifications count every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(getUnseenEmployerNotificationsCount({
      categories: CATEGORIES.EMPLOYER_HIRING_EVENT,
    }));
    const delay = 30 * 1000; // 30 seconds
    const intervalId = setInterval(() => dispatch(getUnseenEmployerNotificationsCount({
      categories: CATEGORIES.EMPLOYER_HIRING_EVENT,
    })), delay);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  const onChangeRoute = (item, query = {}) => {
    const { key } = item;
    handleRoute(item, query);
    setMenuItemSelected(key);
  };

  const clearJobsAndJobGroupsFilters = () => {
    dispatch(updateFilterOptions(omit(['job_group_id', 'sub_organization_id'], filterJobsOptions)));
    dispatch(updateJobGroupsFilterOptions(omit(['sub_organization_id'], filterJobGroupsOptions)));
  };

  const secondaryNavigationOnChangeHandler = (employer, callback, shouldClearFilter = true) => {
    if (shouldClearFilter) clearJobsAndJobGroupsFilters();
    const { id } = employer;
    dispatch(setCurrentEmployer(id));
    if (typeof callback === 'function') callback();
    appendEmployerIdInRouteParams(id);
  };

  const handleOnClickViewProfile = () => {
    routeToProfile();
  };

  const toggleSecondaryNavOpen = () => setSecondaryNavOpen(!secondaryNavOpen);
  const toggleTertiaryNavOpen = () => setTertiaryNavOpen(!tertiaryNavOpen);

  const hamburgerMenuHandler = () => {
    toggleSecondaryNavOpen();
  };

  useEffect(() => {
    const pathname = router?.pathname?.replace(/\/\[[a-zA-Z]+\]/g, '');
    setMenuItemSelected(ROUTE_TO_KEY_MAP[pathname]);
  }, [pathname]);

  useEffect(() => {
    if (currentEmployerId) setCurrentEmployerSelected(currentEmployerId);
  }, [currentEmployerId]);

  /** Hook to disable scrolling when the secondary navigation drawer is open.
   *  The hook also needs to set the position of the screen to fixed when the
   *  drawer is open. This prevents the background of the screen from being scroll-able.
   *  Whenever a physical mouse is connected, an additional 15px of padding is needed
   *  in order to account for the vertical scroll bar that disappears when scrolling
   *  is disabled.
   */
  useEffect(() => {
    const cleanup = () => {
      if (window.innerWidth === document.body.clientWidth) {
        document.body.style.paddingRight = null;
      }
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    };
    if (secondaryNavOpen) {
      if (window.innerWidth > document.body.clientWidth) {
        document.body.style.paddingRight = '15px';
      }
      const scrollY = document.body.style.top;
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    } else {
      cleanup();
    }
  }, [secondaryNavOpen]);

  useEffect(() => {
    setShowHamburgerMenu(isMobile);
  }, [isMobile]);

  const hamburger = (
    <SVGIconWrapper onClick={hamburgerMenuHandler}>
      <SVGIcon
        size="1.66em"
        svgChild={BarsRebrandLightSVG}
        color={rebrandStyles.color.jobcaseBlackColor}
      />
    </SVGIconWrapper>
  );

  const isSecondaryNav = showSecondaryNavigation && history.length > 0 && showHamburgerMenu;

  if (!isMobile) {
    return (
      <SecondaryNavigation
        currentEmployerSelected={currentEmployerSelected}
        employers={employers}
        handleRoute={onChangeRoute}
        isOpen={secondaryNavOpen}
        menuItemSelected={menuItemSelected}
        onChange={secondaryNavigationOnChangeHandler}
        setCurrentEmployerSelected={setCurrentEmployerSelected}
        showSidebar={showSidebar}
        toggleOpen={toggleSecondaryNavOpen}
      />
    );
  }

  return (
    <>
      <PrimaryNavigation id="primary-navigation" isStickyNav isSecondaryNav={isSecondaryNav}>
        {
          isSecondaryNav
            ? (
              <MobileModalHeaderStyled
                onClickBack={onClickBack || (() => dispatch(routePageBack()))}
                backTitle={backTitle}
                className="mb-styled-header"
              />
            ) : (
              <NavContainer>
                <StyledRow>
                  {showHamburgerMenu && (
                    <Column xs3={{ span: 2, order: 0 }}>
                      {hamburger}
                    </Column>
                  )}
                  <StyledColumn xs3={{ span: 8, order: 0 }}>
                    <FlexWrapperLeft>
                      <ImageButton onClick={routeToEmployerCenter}>
                        <Image src="/static-ec/jobcase-for-employers-indigo.svg" alt="Jobcase employer center" />
                      </ImageButton>
                    </FlexWrapperLeft>
                  </StyledColumn>
                  <Column xs3={{ span: showHamburgerMenu ? 2 : 4, order: 1 }}>
                    <FlexWrapperRight>
                      {showProfileMenu && (
                        <>
                          {showHamburgerMenu ? (
                            <StyledAvatarWrapper>
                              <Avatar
                                altText={(currentProfile && currentProfile?.profile_photo) ? `${fullName(currentProfile)} Profile Picture` : 'Placeholder Profile Picture'}
                                imageSrc={currentProfile?.profile_photo || '/static-ec/profile-placeholder.png'}
                                onClickAvatar={toggleTertiaryNavOpen}
                                resizeImage={currentProfile?.profile_photo && config.cloudinaryEnabled}
                                sizeVariant={AVATAR_SIZES.XS2}
                                badge={unseenNotificationCount > 0 ? <AvatarDot /> : null}
                                badgePosition={Avatar.BADGE_POSITIONS.BOTTOM_LEFT}
                              />
                            </StyledAvatarWrapper>
                          ) : (
                            <ProfileTooltipMenuWrapper>
                              <ProfileTooltipMenu
                                imageSrc={currentProfile?.profile_photo || '/static-ec/profile-placeholder.png'}
                                menuItems={[
                                  {
                                    onClick: handleOnClickViewProfile,
                                    text: 'View Profile',
                                  },
                                  ...MENU_ITEMS,
                                ]}
                                title={fullName(currentProfile)}
                              />
                              {unseenNotificationCount > 0 && <AvatarDot />}
                            </ProfileTooltipMenuWrapper>
                          )}
                        </>
                      )}
                    </FlexWrapperRight>
                  </Column>
                </StyledRow>
              </NavContainer>
            )
        }
      </PrimaryNavigation>
      <SecondaryNavigation
        currentEmployerSelected={currentEmployerSelected}
        employers={employers}
        handleRoute={onChangeRoute}
        isOpen={secondaryNavOpen}
        menuItemSelected={menuItemSelected}
        onChange={secondaryNavigationOnChangeHandler}
        setCurrentEmployerSelected={setCurrentEmployerSelected}
        showSidebar={showSidebar}
        toggleOpen={toggleSecondaryNavOpen}
      />
      <TertiaryNavigation
        toggleOpen={toggleTertiaryNavOpen}
        isOpen={tertiaryNavOpen}
      />
    </>
  );
};

EmployerNavigation.propTypes = {
  showSidebar: PropTypes.bool,
  showProfileMenu: PropTypes.bool,
  showSecondaryNavigation: PropTypes.bool,
  onClickBack: PropTypes.func,
  backTitle: PropTypes.string,
};

EmployerNavigation.defaultProps = {
  showSidebar: true,
  showProfileMenu: true,
  showSecondaryNavigation: false,
  onClickBack: null,
  backTitle: null,
};

export default memo(EmployerNavigation);
